import type {
  SimulationEntityState,
  SimulationFrame,
  SimulationResult,
  Vector2D,
} from "../types/dynamics";
import type { EntityBody } from "../types/entity";
import type { KineticCoreConfig } from "../types/game-config";
import { getEliminationBoundary, isOutOfBounds } from "./boundary";
import {
  detectCollision,
  resolveCollision,
  separateOverlap,
} from "./collision";
import { allEntitiesStopped, applyFriction } from "./friction";

interface MutableEntityState {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  mass: number;
  eliminated: boolean;
}

function toMutableStates(
  entities: ReadonlyArray<EntityBody>,
  impulseEntityId: string,
  impulse: Vector2D,
): MutableEntityState[] {
  return entities
    .filter((entity) => !entity.eliminated)
    .map((entity) => ({
      id: entity.id,
      x: entity.x,
      y: entity.y,
      vx: entity.id === impulseEntityId ? impulse.x : 0,
      vy: entity.id === impulseEntityId ? impulse.y : 0,
      radius: entity.radius,
      mass: entity.mass,
      eliminated: false,
    }));
}

function captureFrame(
  step: number,
  states: ReadonlyArray<MutableEntityState>,
): SimulationFrame {
  return {
    step,
    entities: states.map((state) => ({
      id: state.id,
      x: state.x,
      y: state.y,
      vx: state.vx,
      vy: state.vy,
      eliminated: state.eliminated,
    })),
  };
}

export function simulateImpulse(
  entities: ReadonlyArray<EntityBody>,
  impulseEntityId: string,
  impulse: Vector2D,
  config: KineticCoreConfig,
): SimulationResult {
  const states = toMutableStates(entities, impulseEntityId, impulse);
  const boundary = getEliminationBoundary(config.space);
  const frames: SimulationFrame[] = [captureFrame(0, states)];
  const eliminatedEntityIds: string[] = [];

  for (let step = 1; step <= config.dynamics.maxSimulationSteps; step++) {
    const activeStates = states.filter((state) => !state.eliminated);

    for (const state of activeStates) {
      state.x += state.vx * config.dynamics.timeStep;
      state.y += state.vy * config.dynamics.timeStep;
    }

    for (let i = 0; i < activeStates.length; i++) {
      for (let j = i + 1; j < activeStates.length; j++) {
        const a = activeStates[i];
        const b = activeStates[j];
        if (!a || !b) continue;

        if (detectCollision(a, b)) {
          const separated = separateOverlap(a, b);
          a.x = separated.a.x;
          a.y = separated.a.y;
          b.x = separated.b.x;
          b.y = separated.b.y;

          const resolved = resolveCollision(
            a,
            b,
            config.dynamics.restitutionCoefficient,
          );
          a.vx = resolved.a.vx;
          a.vy = resolved.a.vy;
          b.vx = resolved.b.vx;
          b.vy = resolved.b.vy;
        }
      }
    }

    for (const state of activeStates) {
      if (!state.eliminated && isOutOfBounds(state.x, state.y, boundary)) {
        state.eliminated = true;
        state.vx = 0;
        state.vy = 0;
        eliminatedEntityIds.push(state.id);
      }
    }

    for (const state of activeStates) {
      if (state.eliminated) continue;
      const frictionApplied = applyFriction(
        state.vx,
        state.vy,
        config.dynamics,
      );
      state.vx = frictionApplied.vx;
      state.vy = frictionApplied.vy;
    }

    frames.push(captureFrame(step, states));

    const remaining = states.filter((state) => !state.eliminated);
    if (allEntitiesStopped(remaining, config.dynamics)) {
      break;
    }
  }

  const finalEntityStates: SimulationEntityState[] = states.map((state) => ({
    id: state.id,
    x: state.x,
    y: state.y,
    vx: state.vx,
    vy: state.vy,
    eliminated: state.eliminated,
  }));

  return {
    recording: {
      frames,
      totalSteps: frames.length - 1,
      eliminatedEntityIds,
    },
    finalEntityStates,
  };
}
